import { createClient } from '@supabase/supabase-js';

// Configurações do Supabase via variáveis de ambiente
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://apnimqlklbywrqetnamf.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Auth helpers
export const auth = {
  signUp: async (email: string, password: string, metadata?: any) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });
    return { data, error };
  },

  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  getCurrentUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  resetPassword: async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email);
    return { data, error };
  },

  updatePassword: async (password: string) => {
    const { data, error } = await supabase.auth.updateUser({
      password,
    });
    return { data, error };
  },
};

// Database helpers
export const db = {
  // Usuarios
  getUsuarios: async (filters?: any) => {
    let query = supabase.from('usuarios').select('*');
    
    if (filters?.perfil) {
      query = query.in('perfil', filters.perfil);
    }
    if (filters?.ativo !== undefined) {
      query = query.eq('ativo', filters.ativo);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });
    return { data, error };
  },

  getUsuario: async (id: string) => {
    const { data, error } = await supabase
      .from('usuarios')
      .select('*')
      .eq('id', id)
      .single();
    return { data, error };
  },

  updateUsuario: async (id: string, updates: any) => {
    const { data, error } = await supabase
      .from('usuarios')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  // Filiais
  getFiliais: async (ativasOnly: boolean = true) => {
    let query = supabase.from('filiais').select('*');
    
    if (ativasOnly) {
      query = query.eq('ativa', true);
    }
    
    const { data, error } = await query.order('codigo');
    return { data, error };
  },

  getFilial: async (id: string) => {
    const { data, error } = await supabase
      .from('filiais')
      .select('*')
      .eq('id', id)
      .single();
    return { data, error };
  },

  createFilial: async (filial: any) => {
    const { data, error } = await supabase
      .from('filiais')
      .insert(filial)
      .select()
      .single();
    return { data, error };
  },

  updateFilial: async (id: string, updates: any) => {
    const { data, error } = await supabase
      .from('filiais')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  // Agendamentos
  getAgendamentos: async (filters?: any) => {
    let query = supabase
      .from('agendamentos')
      .select(`
        *,
        usuario:usuarios(nome, email, perfil),
        filial:filiais(codigo, nome)
      `);
    
    if (filters?.status) {
      query = query.in('status', filters.status);
    }
    if (filters?.tipo_produto) {
      query = query.in('tipo_produto', filters.tipo_produto);
    }
    if (filters?.filial_id) {
      query = query.in('filial_id', filters.filial_id);
    }
    if (filters?.data_inicio) {
      query = query.gte('data', filters.data_inicio);
    }
    if (filters?.data_fim) {
      query = query.lte('data', filters.data_fim);
    }
    if (filters?.usuario_id) {
      query = query.eq('usuario_id', filters.usuario_id);
    }
    
    const { data, error } = await query.order('data', { ascending: false });
    return { data, error };
  },

  getAgendamento: async (id: string) => {
    const { data, error } = await supabase
      .from('agendamentos')
      .select(`
        *,
        usuario:usuarios(nome, email, perfil),
        filial:filiais(codigo, nome)
      `)
      .eq('id', id)
      .single();
    return { data, error };
  },

  createAgendamento: async (agendamento: any) => {
    const { data, error } = await supabase
      .from('agendamentos')
      .insert(agendamento)
      .select()
      .single();
    return { data, error };
  },

  updateAgendamento: async (id: string, updates: any) => {
    const { data, error } = await supabase
      .from('agendamentos')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  deleteAgendamento: async (id: string) => {
    const { error } = await supabase
      .from('agendamentos')
      .delete()
      .eq('id', id);
    return { error };
  },

  // Cargas
  getCargas: async (filters?: any) => {
    let query = supabase
      .from('cargas')
      .select(`
        *,
        agendamento:agendamentos(data, status, tipo_produto),
        usuario:usuarios(nome, email),
        filial:filiais(codigo, nome)
      `);
    
    if (filters?.status) {
      query = query.in('status', filters.status);
    }
    if (filters?.filial_id) {
      query = query.in('filial_id', filters.filial_id);
    }
    if (filters?.usuario_id) {
      query = query.eq('usuario_id', filters.usuario_id);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });
    return { data, error };
  },

  getCarga: async (id: string) => {
    const { data, error } = await supabase
      .from('cargas')
      .select(`
        *,
        agendamento:agendamentos(*),
        usuario:usuarios(nome, email),
        filial:filiais(codigo, nome)
      `)
      .eq('id', id)
      .single();
    return { data, error };
  },

  createCarga: async (carga: any) => {
    const { data, error } = await supabase
      .from('cargas')
      .insert(carga)
      .select()
      .single();
    return { data, error };
  },

  updateCarga: async (id: string, updates: any) => {
    const { data, error } = await supabase
      .from('cargas')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    return { data, error };
  },

  // Notificações
  getNotificacoes: async (usuarioId: string, apenasNaoLidas: boolean = false) => {
    let query = supabase
      .from('notificacoes')
      .select('*')
      .eq('usuario_id', usuarioId);
    
    if (apenasNaoLidas) {
      query = query.eq('lida', false);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });
    return { data, error };
  },

  createNotificacao: async (notificacao: any) => {
    const { data, error } = await supabase
      .from('notificacoes')
      .insert(notificacao)
      .select()
      .single();
    return { data, error };
  },

  marcarNotificacaoLida: async (id: string) => {
    const { error } = await supabase
      .from('notificacoes')
      .update({ lida: true })
      .eq('id', id);
    return { error };
  },

  marcarTodasNotificacoesLidas: async (usuarioId: string) => {
    const { error } = await supabase
      .from('notificacoes')
      .update({ lida: true })
      .eq('usuario_id', usuarioId)
      .eq('lida', false);
    return { error };
  },

  // Dias fechados
  getDiasFechados: async (filters?: any) => {
    let query = supabase
      .from('controle_fechamento')
      .select(`
        *,
        filial:filiais(codigo, nome)
      `);
    
    if (filters?.filial_id) {
      query = query.eq('filial_id', filters.filial_id);
    }
    if (filters?.tipo_fechamento) {
      query = query.eq('tipo_fechamento', filters.tipo_fechamento);
    }
    
    const { data, error } = await query.order('data');
    return { data, error };
  },

  createDiaFechado: async (diaFechado: any) => {
    const { data, error } = await supabase
      .from('controle_fechamento')
      .insert(diaFechado)
      .select()
      .single();
    return { data, error };
  },

  deleteDiaFechado: async (id: string) => {
    const { error } = await supabase
      .from('controle_fechamento')
      .delete()
      .eq('id', id);
    return { error };
  },

  // Dashboard stats
  getDashboardStats: async () => {
    const { data, error } = await supabase.rpc('get_dashboard_stats');
    return { data, error };
  },
};

// Storage helpers
export const storage = {
  uploadFile: async (bucket: string, path: string, file: File) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file);
    return { data, error };
  },

  getPublicUrl: (bucket: string, path: string) => {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);
    return data.publicUrl;
  },

  deleteFile: async (bucket: string, path: string) => {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);
    return { error };
  },
};

// Realtime subscriptions
export const subscribeToTable = (
  table: string,
  callback: (payload: any) => void
) => {
  return supabase
    .channel(`public:${table}`)
    .on('postgres_changes', 
      { event: '*', schema: 'public', table }, 
      callback
    )
    .subscribe();
};

export default supabase;
